import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

import { EASE_OUT_CSS } from '@/lib/ease';
import { cn } from '@/lib/utils';

/** Hält die frisch begonnene Zeile im Zwilling offen, bis ein Zeichen kommt. */
const ZERO_WIDTH_SPACE = '\u200b';

/**
 * Das mitwachsende Eingabefeld des Chats — der Textkasten aus beuis
 * `PromptInput`, herausgelöst und um das gebracht, was dort fest verdrahtet
 * ist (Modellauswahl, eigener Absende-Knopf, eigener Absende-Pfad).
 *
 * **Warum ein Zwilling statt `scrollHeight` des Feldes.** Wer die Höhe eines
 * `textarea` messen will, muss sie erst auf `auto` zurücksetzen — und genau das
 * lässt die Seite bei jedem Tastendruck kurz springen. Stattdessen steht ein
 * unsichtbarer Zwilling mit demselben Kasten und derselben Schrift über dem
 * Feld; gemessen wird er, gesetzt wird das Feld. Der Zwilling trägt ein
 * Null-Breiten-Leerzeichen am Ende, damit eine frisch begonnene Zeile schon
 * zählt, bevor ein Zeichen darinsteht.
 *
 * Die Höhe wandert zwischen `minRows` und `maxRows`; darüber hinaus scrollt das
 * Feld. Der Wechsel ist weich, `prefers-reduced-motion` schaltet ihn ab.
 */
export interface ChatPromptFieldProps
    extends Omit<
        React.TextareaHTMLAttributes<HTMLTextAreaElement>,
        'value' | 'onChange' | 'rows' | 'style'
    > {
    value: string;
    onValueChange: (value: string) => void;
    minRows?: number;
    maxRows?: number;
    /** Zeilenhöhe in Pixeln — muss zur gesetzten `leading-*`-Klasse passen. */
    lineHeight?: number;
}

export const ChatPromptField = forwardRef<HTMLTextAreaElement, ChatPromptFieldProps>(
    function ChatPromptField(
        {
            value,
            onValueChange,
            minRows = 2,
            maxRows = 8,
            lineHeight = 24,
            className,
            ...textareaProperties
        },
        forwardedRef,
    ) {
        const textareaRef = useRef<HTMLTextAreaElement | null>(null);
        const measurementRef = useRef<HTMLDivElement | null>(null);

        const setRefs = useCallback(
            (node: HTMLTextAreaElement | null) => {
                textareaRef.current = node;
                if (typeof forwardedRef === 'function') {
                    forwardedRef(node);
                } else if (forwardedRef) {
                    (forwardedRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
                }
            },
            [forwardedRef],
        );

        const resize = useCallback(() => {
            const textarea = textareaRef.current;
            const measurement = measurementRef.current;
            if (!textarea || !measurement) return;

            const next = Math.min(
                Math.max(measurement.scrollHeight, minRows * lineHeight),
                maxRows * lineHeight,
            );
            const height = `${next}px`;
            if (textarea.style.height !== height) textarea.style.height = height;
        }, [lineHeight, maxRows, minRows]);

        useLayoutEffect(() => {
            resize();
        }, [resize, value]);

        useEffect(() => {
            const textarea = textareaRef.current;
            if (!textarea || typeof ResizeObserver === 'undefined') return;
            const observer = new ResizeObserver(resize);
            observer.observe(textarea);
            return () => observer.disconnect();
        }, [resize]);

        return (
            <div className='relative'>
                <div
                    ref={measurementRef}
                    aria-hidden='true'
                    className={cn(
                        'pointer-events-none invisible absolute left-0 right-0 top-0 whitespace-pre-wrap [overflow-wrap:break-word]',
                        className,
                    )}
                >
                    {`${value}${ZERO_WIDTH_SPACE}`}
                </div>
                <textarea
                    ref={setRefs}
                    rows={minRows}
                    value={value}
                    onChange={(event) => onValueChange(event.target.value)}
                    {...textareaProperties}
                    className={cn(
                        'block w-full resize-none overflow-y-auto border-none bg-transparent outline-none',
                        'transition-[height] duration-150 motion-reduce:transition-none',
                        className,
                    )}
                    style={{ transitionTimingFunction: EASE_OUT_CSS }}
                />
            </div>
        );
    },
);
